import React, { useState, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Fab,
  TablePagination,
  TextField,
  InputAdornment
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon
} from "@mui/icons-material";

import MaintenanceTypesForm from "./MaintenanceTypesForm";
import { useMaintenanceTypes } from "../../../hooks/maintenance/useMaintenanceTypes";
import { useAuthContext } from "../../../context/AuthProvider";

const MaintenanceTypesDashboard = () => {
  const [formState, setFormState] = useState({ open: false, maintenanceType: null });
  const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, maintenanceType: null });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  const { user } = useAuthContext();
  const {
    maintenanceTypes,
    isLoading,
    error,
    createMutation,
    updateMutation,
    deleteMutation
  } = useMaintenanceTypes();

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredTypes = maintenanceTypes?.filter(
    (type) =>
      type.tipmannom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      type.tipmandes?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const showAlert = (message, severity = "success") => {
    setAlert({ open: true, message, severity });
  };

  const handleOpenForm = (maintenanceType = null) => {
    setFormState({ open: true, maintenanceType });
  };

  const handleCloseForm = () => {
    setFormState({ open: false, maintenanceType: null });
  };

  const handleSubmit = useCallback(
    (data) => {
      if (!user) {
        showAlert("Debe iniciar sesión para realizar esta acción", "error");
        return;
      }

      if (formState.maintenanceType) {
        updateMutation.mutate(
          { id: formState.maintenanceType.tipmanid, data },
          {
            onSuccess: () => {
              showAlert("Tipo de mantenimiento actualizado correctamente");
              handleCloseForm();
            },
            onError: (error) => {
              showAlert(error?.message || "Error al actualizar", "error");
            }
          }
        );
      } else {
        createMutation.mutate(data, {
          onSuccess: () => {
            showAlert("Tipo de mantenimiento creado correctamente");
            handleCloseForm();
          },
          onError: (error) => {
            showAlert(error?.message || "Error al crear", "error");
          }
        });
      }
    },
    [user, formState.maintenanceType, createMutation, updateMutation]
  );

  const handleConfirmDelete = () => {
    if (confirmDialog.maintenanceType) {
      deleteMutation.mutate(confirmDialog.maintenanceType.tipmanid, {
        onSuccess: () => {
          showAlert("Tipo de mantenimiento eliminado correctamente");
          setConfirmDialog({ open: false, maintenanceType: null });
        },
        onError: (error) => {
          showAlert(error?.response?.data?.error || "Error al eliminar", "error");
        }
      });
    }
  };

  return (
    <Paper sx={{ p: 3, backgroundColor: "#f9f9f9", borderRadius: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={600}>
          Tipos de Mantenimiento
        </Typography>
        <TextField
          variant="outlined"
          size="small"
          placeholder="Buscar..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">
          Error al cargar los tipos de mantenimiento: {error.message}
        </Typography>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead sx={{ backgroundColor: "#e0e0e0" }}>
              <TableRow>
                <TableCell><strong>Nombre</strong></TableCell>
                <TableCell><strong>Descripción</strong></TableCell>
                <TableCell align="center"><strong>Acciones</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTypes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((type) => (
                <TableRow key={type.tipmanid} hover>
                  <TableCell>{type.tipmannom}</TableCell>
                  <TableCell>{type.tipmandes || "Sin descripción"}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleOpenForm(type)}>
                        <EditIcon color="primary" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton size="small" onClick={() => setConfirmDialog({ open: true, maintenanceType: type })}>
                        <DeleteIcon color="error" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredTypes.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}

      <Fab color="primary" sx={{ position: "fixed", bottom: 20, right: 20 }} onClick={() => handleOpenForm()}>
        <AddIcon />
      </Fab>

      {formState.open && (
        <MaintenanceTypesForm
          open={formState.open}
          handleClose={handleCloseForm}
          onSubmit={handleSubmit}
          defaultValues={formState.maintenanceType || {}}
        />
      )}

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, maintenanceType: null })}
      >
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro que desea eliminar el tipo de mantenimiento "{confirmDialog.maintenanceType?.tipmannom}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, maintenanceType: null })} color="secondary">
            Cancelar
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={alert.open} autoHideDuration={4000} onClose={() => setAlert({ ...alert, open: false })}>
        <Alert severity={alert.severity}>{alert.message}</Alert>
      </Snackbar>
    </Paper>
  );
};

export default MaintenanceTypesDashboard;
